import { ApiError, apiFetch } from "@/lib/auth";

/** Connector row (change fix-local-connections). Never carries the token. */
export type ConnectorOut = {
  id: string;
  name: string;
  user_id: string;
  folder_id?: string | null;
  status: string;
  /** Live channel in the hub right now (fix-session-egress-and-clipboard D5).
   * `status` is what the DB last saw; this is what can carry traffic now. */
  online: boolean;
  last_seen_at: string | null;
  created_at: string;
};

/** Manual-install one-liner for the connector role per OS (change
 * add-connector-manual-install, design D3). */
export type OneLiners = {
  bash: string;
  powershell: string;
  darwin: string;
};

/** One-shot registration response: the clear token is shown exactly once. */
export type ConnectorRegistered = {
  connector_id: string;
  name: string;
  token: string;
  one_liners: OneLiners;
  status: string;
  folder_id?: string | null;
};

export function listConnectors(folderId?: string | null): Promise<ConnectorOut[]> {
  const qs = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : "";
  return apiFetch<ConnectorOut[]>(`/api/connectors${qs}`);
}

export function createConnector(
  name: string,
  folderId?: string | null,
): Promise<ConnectorRegistered> {
  return apiFetch<ConnectorRegistered>("/api/connectors", {
    method: "POST",
    body: JSON.stringify({
      name,
      ...(folderId ? { folder_id: folderId } : {}),
    }),
  });
}

export function updateConnector(
  id: string,
  input: { name?: string; folder_id?: string | null },
): Promise<ConnectorOut> {
  return apiFetch<ConnectorOut>(`/api/connectors/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function revokeConnector(id: string): Promise<void> {
  await apiFetch<undefined>(`/api/connectors/${id}`, { method: "DELETE" });
}

/** Uninstall the service on its host, then revoke the record (owner/admin).
 * 409 when the connector is offline or does not support remote uninstall. */
export async function uninstallConnector(id: string): Promise<void> {
  await apiFetch<undefined>(`/api/connectors/${id}/uninstall`, {
    method: "POST",
  });
}

/** One probe origin for GET /connections/{id}/visibility. */
export type VisibilityOrigin = {
  origin: "server" | "connector";
  connector_id?: string;
  name?: string;
  ok: boolean;
  latency_ms?: number;
  error?: string;
};

export type VisibilityResult = {
  connection_id: string;
  origins: VisibilityOrigin[];
};

export function connectionVisibility(id: string): Promise<VisibilityResult> {
  return apiFetch<VisibilityResult>(`/api/connections/${id}/visibility`);
}

export { ApiError };